/**
 * How API requests carry identity.
 *
 * The web app authenticates with a Django session cookie. The Telegram Mini App
 * cannot: on Telegram Desktop and Telegram Web the app runs inside a cross-site
 * iframe, where `SameSite=Lax` makes the browser drop the session and CSRF
 * cookies. It works on mobile (a top-level webview) and fails on desktop —
 * exactly the kind of split that hides during testing.
 *
 * So the mini app uses a bearer token instead, and this module is the single
 * place that knows which mode is active. Call sites ask for headers and a
 * credentials mode; they never branch on platform themselves.
 */

const TOKEN_STORAGE_KEY = 'tg_auth_token';

/** @type {string|null} */
let token = null;
let restored = false;

/**
 * Reads the persisted token once per page load.
 * Storage can throw outright in a locked-down webview, so failure is silent and
 * simply leaves the app on the cookie path.
 */
function restore() {
    if (restored) return;
    restored = true;
    try {
        token = localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
        token = null;
    }
}

/** True when requests should travel on a bearer token rather than the cookie. */
export function is_token_auth() {
    restore();
    return token !== null;
}

/**
 * Adopts a bearer token for every subsequent request and persists it.
 * @param {string} value
 */
export function set_auth_token(value) {
    token = value || null;
    restored = true;
    try {
        if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
        else localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
        // In-memory token still works for this session.
    }
}

export function clear_auth_token() {
    set_auth_token(null);
}

/**
 * Auth headers for a request — empty on the cookie path.
 * @returns {Record<string, string>}
 */
export function auth_headers() {
    restore();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Credentials mode to pair with {@link auth_headers}.
 *
 * `omit` on the token path: sending the cookie from inside a third-party frame
 * either does nothing or, worse, authenticates a different user than the token
 * names. One credential per request keeps the two surfaces from crossing.
 *
 * @returns {RequestCredentials}
 */
export function auth_credentials() {
    restore();
    return token ? 'omit' : 'same-origin';
}

/**
 * Merges auth into a fetch init. The single call sites should use.
 * @param {RequestInit} [options]
 * @returns {RequestInit}
 */
export function with_auth(options = {}) {
    return {
        ...options,
        headers: { ...(options.headers || {}), ...auth_headers() },
        credentials: auth_credentials(),
    };
}
